/**
 * Step 1 -- US Engagements master list (f10.4), rendered inside f10.3's step
 * frame through `STEP_BODIES`. Follows `cycle-step.html`:
 *
 *   upload -> process -> what did not match -> decide -> figures -> accept
 *
 * The screen shows the newest batch that has not been discarded. Uploading
 * answers `202` with a parse job; accepting answers `202` with a commit job.
 * Both are watched through the step's own job route, and on success the
 * batch, the rail and (after a commit) the client list are refetched.
 */

import { useEffect, useRef, useState } from 'react';
import { Check, X } from 'lucide-react';

import type { CycleWorkflowStep } from '@/api/client';
import {
  isJobTerminal,
  useCommitUsEngagementsBatch,
  useDiscardUsEngagementsBatch,
  useRefreshAfterUsEngagementsCommit,
  useRefreshUsEngagements,
  useUploadUsEngagementsBatch,
  useUsEngagementsBatches,
  useUsEngagementsJob,
  useUsEngagementsReview,
} from '@/api/queries';
import { JobProgress } from '@/jobs/JobProgress';

import { FileDroppedCard, ProcessingCard, UsEngagementsDropzone } from './BatchFileCards';
import { CommitDialog } from './CommitDialog';
import { FiguresCard } from './FiguresCard';
import { UnmatchedCard } from './UnmatchedCard';
import { hasStagedItems, isParsing, refusalMessage } from './usEngagementsView';

interface WatchedJob {
  id: string;
  kind: 'parse' | 'commit';
}

export function UsEngagementsStep({ cycleId }: { cycleId: string; step: CycleWorkflowStep }) {
  const batches = useUsEngagementsBatches(cycleId);
  const upload = useUploadUsEngagementsBatch(cycleId);
  const [watched, setWatched] = useState<WatchedJob | null>(null);

  const shown = batches.data?.batches.find((batch) => batch.state.code !== 'DISCARDED');
  const parsing = shown ? isParsing(shown.state.code) : false;
  const review = useUsEngagementsReview(cycleId, shown && !parsing ? shown.id : undefined);
  const job = useUsEngagementsJob(cycleId, watched?.id);

  useJobCompletion(cycleId, watched, job.data?.state);

  const startUpload = (file: File) =>
    upload.mutate(file, { onSuccess: (accepted) => setWatched({ id: accepted.job_id, kind: 'parse' }) });

  if (batches.isPending) return <p className="hint">Loading step 1…</p>;
  if (batches.isError) {
    return <ErrorAlert message={refusalMessage(batches.error) ?? 'Step 1 could not be loaded.'} />;
  }

  const jobCard = job.data && (!isJobTerminal(job.data.state) || job.data.state === 'FAILED') && (
    <div className="card">
      <div className="card-body">
        <JobProgress
          job={job.data}
          title={watched?.kind === 'commit' ? 'Applying confirmations' : 'Reading the US Engagements master'}
        />
      </div>
    </div>
  );

  return (
    <div className="stack">
      {upload.error ? <ErrorAlert message={refusalMessage(upload.error)} /> : null}
      {jobCard}

      {!shown && <UsEngagementsDropzone onFile={startUpload} busy={upload.isPending} />}

      {shown && parsing && !jobCard && (
        <p className="hint" role="status">
          Reading {shown.original_filename ?? 'the uploaded file'}…
        </p>
      )}

      {review.isError && <ErrorAlert message={refusalMessage(review.error)} />}

      {review.data && (
        <>
          <FileDroppedCard review={review.data} onReplace={startUpload} busy={upload.isPending} />
          <ProcessingCard review={review.data} />
          {hasStagedItems(review.data.batch.state.code) && (
            <BatchDecisions
              cycleId={cycleId}
              review={review.data}
              onCommitStarted={(jobId) => setWatched({ id: jobId, kind: 'commit' })}
            />
          )}
        </>
      )}
    </div>
  );
}

/** Refetch once a watched job reaches a terminal state -- once per job. */
function useJobCompletion(cycleId: string, watched: WatchedJob | null, state: string | undefined) {
  const refresh = useRefreshUsEngagements(cycleId);
  const refreshAfterCommit = useRefreshAfterUsEngagementsCommit(cycleId);
  const handled = useRef<string | null>(null);

  useEffect(() => {
    if (!watched || !isJobTerminal(state) || handled.current === watched.id) return;
    handled.current = watched.id;
    if (watched.kind === 'commit' && state === 'SUCCEEDED') refreshAfterCommit();
    else refresh();
  }, [watched, state, refresh, refreshAfterCommit]);
}

function BatchDecisions({
  cycleId,
  review,
  onCommitStarted,
}: {
  cycleId: string;
  review: NonNullable<ReturnType<typeof useUsEngagementsReview>['data']>;
  onCommitStarted: (jobId: string) => void;
}) {
  const commit = useCommitUsEngagementsBatch(cycleId, review.batch.id);
  const discard = useDiscardUsEngagementsBatch(cycleId, review.batch.id);
  const [confirming, setConfirming] = useState(false);
  const { capabilities } = review;

  const footer = (capabilities.can_commit || capabilities.can_discard) && (
    <div className="card-foot">
      <span className="hint">
        {capabilities.can_commit
          ? 'Every item is decided. Accepting applies the confirmations and completes step 1.'
          : 'Decide every item above to enable Accept and commit.'}
      </span>
      <div className="btn-row">
        {capabilities.can_discard && (
          <button type="button" className="btn btn-secondary" disabled={discard.isPending}
            onClick={() => discard.mutate()}>
            <X aria-hidden="true" /> Discard batch
          </button>
        )}
        <button type="button" className="btn btn-primary" disabled={!capabilities.can_commit}
          onClick={() => setConfirming(true)}>
          <Check aria-hidden="true" /> Accept and commit
        </button>
      </div>
    </div>
  );

  return (
    <>
      <UnmatchedCard cycleId={cycleId} review={review} />
      {discard.error ? <ErrorAlert message={refusalMessage(discard.error)} /> : null}
      <FiguresCard figures={review.figures} footer={footer || undefined} />
      {confirming && (
        <CommitDialog
          review={review}
          busy={commit.isPending}
          error={commit.error}
          onClose={() => {
            setConfirming(false);
            commit.reset();
          }}
          onConfirm={() =>
            commit.mutate(undefined, {
              onSuccess: (accepted) => {
                setConfirming(false);
                onCommitStarted(accepted.job_id);
              },
            })
          }
        />
      )}
    </>
  );
}

function ErrorAlert({ message }: { message: string | null }) {
  if (!message) return null;
  return (
    <div className="alert alert-danger" role="alert">
      <div>
        <div className="a-body">{message}</div>
      </div>
    </div>
  );
}
